/**
 * Helper for Log and Error management.
 * This services must be used with the components/SnackComponent component
 * We need an instance of the Snackcomponent placed at the top level component of the app
 * This static service offers 4 services to show the diferent types os snackbars:
 * createError, createInfo, createWarning and createSuccess and has a message param to change de snackbar text
 */

/** Kind of snackbar to show. Matches the TYPE_* / DIRECTION_INFO constants. */
export type LogType = 'danger' | 'info' | 'success' | 'warning' | 'direction-info';

/** A queued or currently shown log. */
export interface LogEntry {
    message: string;
    type: LogType;
    block: boolean;
    lock: boolean;
}

/** Payload handed to the SnackComponent. */
export interface LogComponentData {
    visible: boolean;
    message: string;
    type: LogType;
    block: boolean;
}

export type LogHandler = (data: LogComponentData) => void;
export type LogCloseHandler = () => void;

/** Error response as returned by axios. */
export interface ErrorResponse {
    status: number;
    data?: {
        error?: string;
        error_description?: string;
    };
}

/** Error as received from axios, or any thrown value with a message. */
export interface RequestError {
    response?: ErrorResponse;
    message?: string;
    /** Código de axios; ERR_CANCELED si la petición se abortó. */
    code?: string;
}

/** Structured error returned to the caller. */
export interface ProcessedError {
    type: 'response' | 'network' | 'canceled' | 'unknown';
    message: string;
    error?: unknown;
}

class LogManager {

    static readonly TYPE_ERROR = 'danger';
    static readonly TYPE_INFO = 'info';
    static readonly TYPE_SUCCESS = 'success';
    static readonly TYPE_WARNING = 'warning';
    static readonly LOG_EVENT = 'LogEvent';
    static readonly DIRECTION_INFO = 'direction-info';

    static logs: LogEntry[] = [];
    static open = false;

    static initialLogData = {
        visible: false,
        message: '',
    }

    static actualLog: LogEntry | undefined;
    static componentHandler: LogHandler | undefined;
    static componentCloseHandler: LogCloseHandler | undefined;
    static blocked = false;
    static locked = false;

    /**
     * method need to connect snack component with log system using the componentHandler
     */
    static setLogHandler = (handler: LogHandler, closeHandler: LogCloseHandler): void => {
        this.componentHandler = handler;
        this.componentCloseHandler = closeHandler;
    }

    /**
     * internal method used to open new log
     * @param lock Si true, el toast solo puede cerrarse via unlock()
     */
    static nextLog = (message: string, type: LogType, block: boolean, lock = false): void => {
        this.open = true;
        this.actualLog = {
            message,
            type,
            block,
            lock,
        }
        if (type === this.DIRECTION_INFO || lock) {
            this.locked = true;
        }
        const data: LogComponentData = {
            visible: true,
            message,
            type,
            block,
        };
        this.componentHandler?.(data);
    }

    /**
     * method used from outside to open new log
     * @param lock Si true, el toast solo puede cerrarse via unlock()
     */
    static createLog = (message: string, type: LogType, block: boolean, lock = false): void => {
        if (this.blocked) {
            // No interrumpir el toast bloqueado actual, pero encolar
            // el mensaje para mostrarlo cuando se cierre.
            this.logs = [{message, type, block, lock}];
            return;
        }

        this.blocked = block;
        if (this.open) {
            // Encolar sin interrumpir el toast actual; aparecerá al cerrarse
            this.logs = [{message, type, block, lock}];
        } else {
            this.nextLog(message, type, block, lock);
        }
    }

    /**
     * method received from snackbar when is closed to detect if we need to open other
     */
    static deleteLog = (): void => {
        this.open = false;
        if (this.logs.length > 0) {
            // La guarda de arriba garantiza que pop() devuelve un elemento.
            const next = this.logs.pop()!;
            this.nextLog(next.message, next.type, next.block, next.lock);
        }
    }

    /**
     * opens error log
     * @param message: string message to send
     * @param lock Si true, el toast solo puede cerrarse via unlock()
     */
    static createError = (message: string, block = false, lock = false): void => {
        this.createLog(message, this.TYPE_ERROR, block, lock);
    }

    /**
     * opens warning log
     * @param message: string message to send
     */
    static createWarning = (message: string, block = false): void => {
        this.createLog(message, this.TYPE_WARNING, block);
    }

    /**
     * opens info log
     * @param message: string message to send
     */
    static createInfo = (message: string, block = false): void => {
        this.createLog(message, this.TYPE_INFO, block);
    }

    /**
     * opens success log
     * @param message: string message to send
     */
    static createSuccess = (message: string, block = false): void => {
        this.createLog(message, this.TYPE_SUCCESS, block);
    }

    /**
     * opens direction info special log
     * @param message: string message to send
     */
    static createDirectionInfo = (message: string, block = true): void => {
        this.createLog(message, this.DIRECTION_INFO, block);
    }

    /**
     * Process network response erors.
     *
     * @param response The error.response object
     * @param error Optional original error object if available
     *
     * @return Structured error object (type, message, error)
     */
    static processResponseError(response: ErrorResponse, error?: RequestError): ProcessedError {
        let message = 'Response ' + response.status;
        if (response.data) {
          if (response.data.error) {
            message += ' ' + response.data.error;
          }
          if (response.data.error_description) {
            message += ': ' + response.data.error_description;
          }
        }
        else if (error) {
          message += ': ' + error.message;
        }
        this.createError(message);
        return {type: 'response', message, error};
    }

    /**
     * Process network request erors.
     *
     * @see https://axios-http.com/docs/handling_errors
     *
     * @return Structured error object (type, message, error)
     */
    static processRequestError(error: unknown): ProcessedError {
      // Lo capturado puede ser cualquier cosa: un error de axios, un Error o un
      // valor suelto. Solo se puede inspeccionar tras comprobar que es objeto.
      const candidate: RequestError = (typeof error === 'object' && error !== null)
        ? error as RequestError
        : {};

      if (candidate.response) {
        return this.processResponseError(candidate.response, candidate);
      }
      // La abortó la propia app (@see SiteAuth.runClientAuthentication): no
      // es un error que haya que enseñar.
      else if (candidate.code === 'ERR_CANCELED') {
        return {type: 'canceled', message: candidate.message ?? 'canceled', error};
      }
      else if (candidate.message) {
        this.createError(candidate.message);
        return {type: 'network', message: candidate.message, error};
      }
      else {
        // SnackComponent pinta el mensaje como hijo de React, asi que tiene que
        // ser una cadena de verdad. El valor original se conserva en .error.
        const message = String(error ?? '');
        this.createError(message);
        return {type: 'unknown', message, error};
      }
    }

    /**
     * Clears any queued logs without showing them.
     * Usar cuando la condición bloqueante termina y los mensajes pendientes deben descartarse.
     */
    static clearLogs = (): void => {
        this.logs = [];
    }

    /*
        * Close the log
     */
    static closeLog(): void {
        if (this.locked) {
            return;
        }
        this.blocked = false;
        this.componentCloseHandler?.();
        this.deleteLog();
    }

    /**
     * Unlock the log and close it
     */
    static unlock(): void {
        this.locked = false;
        this.closeLog();
    }
}


export default LogManager;
